import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { apiService } from '../services/api';
import timezoneService from '../services/timezoneService';
import { DateService } from '../utils/dateService';
import { calculateDurationsWithGapDetection, calculateTotalTime } from '../utils/durationCalculator';
import { format } from 'date-fns-tz';

interface ProductivityDataPoint {
  time: string;
  hour: number;
  productivity: number; // 0-100
  hasData: boolean;
}

interface ProductivityTimelineProps {
  timeRange: string;
  currentDate?: Date;
}

const ProductivityTimeline: React.FC<ProductivityTimelineProps> = ({ timeRange, currentDate }) => {
  const [dataPoints, setDataPoints] = useState<ProductivityDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [userInterval, setUserInterval] = useState<number>(5); // Default 5 minutes

  const loadTimelineData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load user's screenshot interval from AI settings
      try {
        const settings = await apiService.getAISettings();
        const interval = settings.data?.screenshotInterval || 5;
        setUserInterval(interval);
        console.log(`⚙️ ProductivityTimeline: Using interval ${interval} minutes`);
      } catch (error) {
        console.error('Failed to load interval, using default 5:', error);
        setUserInterval(5);
      }
      
      // Normalize currentDate to YYYY-MM-DD string at the start
      const targetDateKey = currentDate 
        ? DateService.toStandardDate(currentDate)
        : timezoneService.getCurrentUserDate();
      
      console.log('📅 ProductivityTimeline: Target date normalized:', targetDateKey);
      
      // Get screenshots data to build productivity graph
      let screenshotParams: any = {};
      
      // Set appropriate limits based on time range
      if (timeRange === '1d') {
        // Use normalized date key
        const dateKey = targetDateKey;
        
        // Convert user's local date to UTC range that covers their full day
        const { startUTC, endUTC } = DateService.userDateToUTCRange(dateKey);
        screenshotParams.startDate = startUTC;
        screenshotParams.endDate = endUTC;
        screenshotParams.limit = 200; // Daily limit
        
        console.log('🔍 Daily view UTC range:', { dateKey, startUTC, endUTC });
      } else if (timeRange === '7d' || timeRange === '1w') {
        // For weekly view, use DateService to get week range
        const startDateKey = DateService.getStartOfWeek(targetDateKey);
        const endDateKey = DateService.getEndOfWeek(targetDateKey);
        
        screenshotParams.startDate = startDateKey;
        screenshotParams.endDate = endDateKey;
        screenshotParams.limit = 500; // Weekly limit
        
        console.log('🔍 Weekly view date range:', { startDateKey, endDateKey });
      } else if (timeRange === '30d' || timeRange === '1m') {
        // For monthly view, use DateService to get month range
        const startDateKey = DateService.getStartOfMonth(targetDateKey);
        const endDateKey = DateService.getEndOfMonth(targetDateKey);
        
        screenshotParams.startDate = startDateKey;
        screenshotParams.endDate = endDateKey;
        screenshotParams.limit = 1000; // Monthly limit - get all screenshots
        
        console.log('🔍 Monthly view date range:', { startDateKey, endDateKey });
      } else {
        screenshotParams.timeRange = timeRange;
        screenshotParams.limit = 200; // Default limit
      }
      
      console.log('🔍 ProductivityTimeline: Loading screenshots with params:', screenshotParams);
      const screenshotsResponse = await apiService.getScreenshots(screenshotParams);
      const allScreenshots = screenshotsResponse.data;

      console.log('📸 ProductivityTimeline: Loaded ALL screenshots from backend:', allScreenshots.length);

      // FRONTEND FILTERING: Filter screenshots by date range in user's timezone
      let startDateKey: string;
      let endDateKey: string;
      
      if (timeRange === '1d') {
        // Use currentDate if provided, otherwise get current date in user's timezone
        const targetDate = currentDate || timezoneService.getCurrentUserDate();
        startDateKey = format(targetDate, 'yyyy-MM-dd');
        endDateKey = startDateKey;
      } else if (timeRange === '7d' || timeRange === '1w') {
        // Use currentDate if provided, otherwise get current date in user's timezone
        const targetDate = currentDate || timezoneService.getCurrentUserDate();
        // Use DateService helpers instead of manual calculations
        startDateKey = DateService.getStartOfWeek(
          typeof targetDate === 'string' ? targetDate : DateService.toStandardDate(targetDate)
        );
        endDateKey = DateService.getEndOfWeek(
          typeof targetDate === 'string' ? targetDate : DateService.toStandardDate(targetDate)
        );
      } else if (timeRange === '30d' || timeRange === '1m') {
        // Use currentDate if provided, otherwise get current date in user's timezone
        const targetDate = currentDate || timezoneService.getCurrentUserDate();
        // Use DateService helpers instead of manual calculations
        const targetDateKey = typeof targetDate === 'string' ? targetDate : DateService.toStandardDate(targetDate);
        startDateKey = DateService.getStartOfMonth(targetDateKey);
        endDateKey = DateService.getEndOfMonth(targetDateKey);
      } else {
        // Default to today in user's timezone
        const today = timezoneService.getCurrentUserDate();
        startDateKey = format(today, 'yyyy-MM-dd');
        endDateKey = startDateKey;
      }
      
      const screenshots = allScreenshots.filter((screenshot: any) => {
        const zonedDate = timezoneService.convertUTCToUserTime(screenshot.timestamp);
        const dateKey = format(zonedDate, 'yyyy-MM-dd');
        return dateKey >= startDateKey && dateKey <= endDateKey;
      });
      
      console.log(`📸 ProductivityTimeline: After frontend filtering (${startDateKey} to ${endDateKey}): ${screenshots.length} screenshots`);
      
      if (screenshots.length > 0) {
        console.log('📸 ProductivityTimeline: Sample screenshot structure:', screenshots.slice(0, 2).map((s: any) => ({
          id: s.id,
          timestamp: s.timestamp,
          activity: s.activity,
          confidence: s.confidence
        })));
      }

      // Convert screenshots to hourly productivity data
      const productivityData = generateProductivityData(screenshots);
      console.log('📊 ProductivityTimeline: Generated data points:', productivityData.dataPoints.length);
      setDataPoints(productivityData.dataPoints);
      setStartTime(productivityData.startTime);
      setEndTime(productivityData.endTime);
    } catch (error) {
      console.error('❌ ProductivityTimeline: Failed to load timeline data:', error);
      setDataPoints([]);
      setStartTime('');
      setEndTime('');
    } finally {
      setLoading(false);
    }
  }, [timeRange, currentDate]);

  useEffect(() => {
    loadTimelineData();
  }, [loadTimelineData]);

  const generateProductivityData = (screenshots: any[]): {
    dataPoints: ProductivityDataPoint[];
    startTime: string;
    endTime: string;
  } => {
    if (!screenshots || screenshots.length === 0) {
      return { dataPoints: [], startTime: '', endTime: '' };
    }

    // Filter out screenshots without valid timestamps only
    const validScreenshots = screenshots.filter(screenshot => {
      const timestamp = screenshot.timestamp || screenshot.createdAt || screenshot.created_at;
      if (!timestamp) return false;
      
      // Just validate that we can parse the timestamp (UTC from API)
      // eslint-disable-next-line no-restricted-syntax
      const screenshotTime = new Date(timestamp).getTime();
      return !isNaN(screenshotTime);
    });

    console.log('Total screenshots:', screenshots.length, 'Valid timestamps:', validScreenshots.length);

    if (validScreenshots.length === 0) {
      return { dataPoints: [], startTime: '', endTime: '' };
    }

    // Sort screenshots by UTC timestamp (chronological order)
    const sortedScreenshots = validScreenshots.sort((a, b) => {
      // eslint-disable-next-line no-restricted-syntax
      const timeA = new Date(a.timestamp || a.createdAt || a.created_at).getTime();
      // eslint-disable-next-line no-restricted-syntax
      const timeB = new Date(b.timestamp || b.createdAt || b.created_at).getTime();
      return timeA - timeB;
    });

    // Parse first and last UTC timestamps
    // eslint-disable-next-line no-restricted-syntax
    const firstTime = new Date(sortedScreenshots[0].timestamp || sortedScreenshots[0].createdAt || sortedScreenshots[0].created_at);
    // eslint-disable-next-line no-restricted-syntax
    const lastTime = new Date(sortedScreenshots[sortedScreenshots.length - 1].timestamp || sortedScreenshots[sortedScreenshots.length - 1].createdAt || sortedScreenshots[sortedScreenshots.length - 1].created_at);
    
    console.log('📊 Time range:', {
      firstTime: firstTime.toISOString(),
      lastTime: lastTime.toISOString(),
      firstFormatted: timezoneService.formatUserTime(timezoneService.convertUTCToUserTime(firstTime.toISOString()), { hour: '2-digit', minute: '2-digit' }),
      lastFormatted: timezoneService.formatUserTime(timezoneService.convertUTCToUserTime(lastTime.toISOString()), { hour: '2-digit', minute: '2-digit' })
    });
    
    // Check if this is weekly or monthly view
    const isWeeklyView = timeRange === '7d' || timeRange === '1w';
    const isMonthlyView = timeRange === '30d' || timeRange === '1m';
    
    if (isWeeklyView) {
      // For weekly view, aggregate by day
      return generateWeeklyData(sortedScreenshots);
    } else if (isMonthlyView) {
      // For monthly view, aggregate by day
      return generateMonthlyData(sortedScreenshots);
    } else {
      // For daily view, use hourly intervals as before
      return generateDailyData(sortedScreenshots, firstTime, lastTime);
    }
  };

  const generateDailyData = (sortedScreenshots: any[], firstTime: Date, lastTime: Date) => {
    // Limit end time to prevent future data, but only for today's data
    // eslint-disable-next-line no-restricted-syntax
    const nowTime = new Date(); // Current time for comparison
    const actualFirstTime = firstTime;
    
    // Check if we're viewing historical data (not today)
    const isHistoricalData = currentDate && DateService.toStandardDate(currentDate) < DateService.getToday();
    
    // Only limit to "now" for today's data, not historical dates
    const actualLastTime = isHistoricalData 
      ? lastTime  // Historical date - use actual last screenshot time
      // eslint-disable-next-line no-restricted-syntax
      : new Date(Math.min(lastTime.getTime(), nowTime.getTime())); // Today - limit to now
    
    // Create timeline from first to actual last time (limited to now)
    const dataPoints: ProductivityDataPoint[] = [];
    const timelineStart = actualFirstTime.getTime();
    const timelineEnd = actualLastTime.getTime();
    
    // Create an array of screenshot data for quick lookup
    const screenshotData = sortedScreenshots.map(screenshot => {
      // eslint-disable-next-line no-restricted-syntax
      const time = new Date(screenshot.timestamp || screenshot.createdAt || screenshot.created_at).getTime(); // Parse UTC timestamp
      
      // Use more nuanced productivity calculation based on multiple factors
      let productivity = 50; // Default medium productivity
      
      // Try to get AI analysis data
      let aiAnalysis: any = {};
      try {
        if (screenshot.ai_analysis) {
          aiAnalysis = typeof screenshot.ai_analysis === 'string' 
            ? JSON.parse(screenshot.ai_analysis) 
            : screenshot.ai_analysis;
        }
      } catch (e) {
        // Use fallback data
      }
      
      // Base productivity on work type and confidence
      const workType = aiAnalysis.workType || screenshot.workType || 'Other';
      const confidence = screenshot.confidence || aiAnalysis.confidence || 50;
      const detectedApps = aiAnalysis.detectedApps || screenshot.detectedApplications || [];
      
      // Calculate productivity based on work type
      if (workType === 'Development' || workType === 'coding') {
        productivity = Math.min(95, 70 + (confidence / 100) * 25); // 70-95%
      } else if (workType === 'Research' || workType === 'research') {
        productivity = Math.min(85, 60 + (confidence / 100) * 25); // 60-85%
      } else if (workType === 'Documentation' || workType === 'documentation') {
        productivity = Math.min(80, 55 + (confidence / 100) * 25); // 55-80%
      } else if (workType === 'Communication' || workType === 'meeting') {
        productivity = Math.min(75, 50 + (confidence / 100) * 25); // 50-75%
      } else if (workType === 'Design' || workType === 'design') {
        productivity = Math.min(90, 65 + (confidence / 100) * 25); // 65-90%
      } else {
        productivity = Math.min(70, 40 + (confidence / 100) * 30); // 40-70%
      }
      
      // Adjust based on detected applications (productivity tools get bonus)
      const productiveApps = ['VS Code', 'Xcode', 'IntelliJ', 'Figma', 'Sketch', 'Photoshop'];
      const hasProductiveApp = detectedApps.some((app: string) => 
        productiveApps.some(prodApp => app.includes(prodApp))
      );
      
      if (hasProductiveApp) {
        productivity = Math.min(100, productivity + 10);
      }
      
      // Add some randomness to avoid completely flat lines (±5%)
      const randomVariation = (Math.random() - 0.5) * 10;
      productivity = Math.max(20, Math.min(100, productivity + randomVariation));
      
      return { time, productivity: Math.round(productivity) };
    });

    // Generate data points for the entire timeline
    const intervalMinutes = 5; // 5-minute intervals
    const intervalMs = intervalMinutes * 60 * 1000;
    // eslint-disable-next-line no-restricted-syntax
    const now = new Date().getTime(); // Current time to filter out future data
    
    // Limit timeline end to current time to prevent future data points
    const actualTimelineEnd = Math.min(timelineEnd, now);
    
    for (let currentTime = timelineStart; currentTime <= actualTimelineEnd; currentTime += intervalMs) {
      // Double check - skip any future data points
      if (currentTime > now) {
        break;
      }
      
      // Check if there's a screenshot within this interval (±2.5 minutes)
      let productivity = 0; // Default to 0% (no activity)
      let hasData = false;
      
      // Look for screenshots within this time interval
      for (const screenshot of screenshotData) {
        if (Math.abs(screenshot.time - currentTime) <= intervalMs / 2) {
          productivity = screenshot.productivity;
          hasData = true;
          break;
        }
      }
      
      // Convert currentTime to user timezone before formatting
      // eslint-disable-next-line no-restricted-syntax
      const userDate = timezoneService.convertUTCToUserTime(new Date(currentTime).toISOString());
      
      // If no screenshot found in this interval, productivity stays 0 and hasData stays false
      dataPoints.push({
        time: timezoneService.formatUserTime(userDate, { hour: '2-digit', minute: '2-digit' }),
        hour: userDate.getHours(),
        productivity, // Will be 0 if no screenshot, actual value if screenshot exists
        hasData // Will be false if no screenshot, true if screenshot exists
      });
    }

    // Format times - firstTime is EARLIEST, lastTime is LATEST (after ascending sort)
    // Convert UTC to user timezone before formatting
    const startTimeStr = timezoneService.formatUserTime(timezoneService.convertUTCToUserTime(firstTime.toISOString()), { hour: '2-digit', minute: '2-digit' });
    const endTimeStr = timezoneService.formatUserTime(timezoneService.convertUTCToUserTime(lastTime.toISOString()), { hour: '2-digit', minute: '2-digit' });

    console.log('📊 RETURN VALUES:', {
      startTimeStr,
      endTimeStr,
      dataPointsCount: dataPoints.length
    });

    return {
      dataPoints,
      startTime: startTimeStr,  // EARLIEST time (firstTime from sorted array)
      endTime: endTimeStr       // LATEST time (lastTime from sorted array)
    };
  };

  const generateWeeklyData = (sortedScreenshots: any[]) => {
    // Group screenshots by date and time period (6 periods per day, 4 hours each)
    const dayTimeGroups: { [key: string]: any[] } = {};
    
    sortedScreenshots.forEach(screenshot => {
      // Convert UTC timestamp to user timezone before extracting date/hour
      const utcTimestamp = screenshot.timestamp || screenshot.createdAt || screenshot.created_at;
      const userDate = timezoneService.convertUTCToUserTime(utcTimestamp);
      const dayKey = DateService.toStandardDate(userDate); // YYYY-MM-DD in user timezone
      const hour = userDate.getHours();
      
      // Divide day into 6 periods of 4 hours each: 0-4, 4-8, 8-12, 12-16, 16-20, 20-24
      let period: number;
      if (hour >= 0 && hour < 4) period = 0;
      else if (hour >= 4 && hour < 8) period = 1;
      else if (hour >= 8 && hour < 12) period = 2;
      else if (hour >= 12 && hour < 16) period = 3;
      else if (hour >= 16 && hour < 20) period = 4;
      else period = 5; // 20-24
      
      const dayTimeKey = `${dayKey}-${period}`;
      
      if (!dayTimeGroups[dayTimeKey]) {
        dayTimeGroups[dayTimeKey] = [];
      }
      dayTimeGroups[dayTimeKey].push(screenshot);
    });

    // Create data points for each time period
    const dataPoints: ProductivityDataPoint[] = [];
    const dayNames = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
    const periodLabels = ['1', '2', '3', '4', '5', '6']; // 6 periods per day
    
    // Get the target week's Monday based on currentDate
    // eslint-disable-next-line no-restricted-syntax
    const targetDate = currentDate || new Date(); // Get current date if not provided
    // eslint-disable-next-line no-restricted-syntax
    const today = new Date(); // Keep today for comparison
    // Use timezone-safe date calculations
    const dayOfWeek = targetDate.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    // eslint-disable-next-line no-restricted-syntax
    const monday = new Date(targetDate); // Clone Date for manipulation
    // Use safer date manipulation to avoid timezone issues
    monday.setUTCDate(targetDate.getUTCDate() - daysFromMonday);
    const currentHour = today.getHours();
    
    // Generate 6 data points for each day of the week
    for (let i = 0; i < 7; i++) {
      // eslint-disable-next-line no-restricted-syntax
      const currentDay = new Date(monday); // Clone Date for each day
      // Use timezone-safe date manipulation
      currentDay.setUTCDate(monday.getUTCDate() + i);
      const dayKey = DateService.toStandardDate(currentDay);
      const isToday = dayKey === DateService.toStandardDate(today);
      const isPastDay = currentDay < today;
      
      for (let periodIndex = 0; periodIndex < 6; periodIndex++) {
        const dayTimeKey = `${dayKey}-${periodIndex}`;
        const periodScreenshots = dayTimeGroups[dayTimeKey] || [];
        
        let productivity = 0;
        let hasData = periodScreenshots.length > 0;
        
        // Check if this time period has passed
        let isPastOrCurrent = false;
        if (isPastDay) {
          isPastOrCurrent = true; // Past days are always valid
        } else if (isToday) {
          // For today, check if the time period has started
          const periodStartHour = periodIndex * 4;
          if (currentHour >= periodStartHour) isPastOrCurrent = true;
        }
        
        if (hasData && isPastOrCurrent) {
          const productivitySum = periodScreenshots.reduce((sum, screenshot) => {
            let screenshotProductivity = 50;
            
            try {
              let aiAnalysis: any = {};
              if (screenshot.ai_analysis) {
                aiAnalysis = typeof screenshot.ai_analysis === 'string' 
                  ? JSON.parse(screenshot.ai_analysis) 
                  : screenshot.ai_analysis;
              }
              
              const workType = aiAnalysis.workType || screenshot.workType || 'Other';
              const confidence = screenshot.confidence || aiAnalysis.confidence || 50;
              
              if (workType === 'Development' || workType === 'coding') {
                screenshotProductivity = Math.min(95, 70 + (confidence / 100) * 25);
              } else if (workType === 'Research' || workType === 'research') {
                screenshotProductivity = Math.min(85, 60 + (confidence / 100) * 25);
              } else if (workType === 'Documentation' || workType === 'documentation') {
                screenshotProductivity = Math.min(80, 55 + (confidence / 100) * 25);
              } else if (workType === 'Communication' || workType === 'meeting') {
                screenshotProductivity = Math.min(75, 50 + (confidence / 100) * 25);
              } else if (workType === 'Design' || workType === 'design') {
                screenshotProductivity = Math.min(90, 65 + (confidence / 100) * 25);
              } else {
                screenshotProductivity = Math.min(70, 40 + (confidence / 100) * 30);
              }
            } catch (e) {
              // Use default
            }
            
            return sum + screenshotProductivity;
          }, 0);
          
          productivity = Math.round(productivitySum / periodScreenshots.length);
        }
        
        // For future time periods, set hasData to false and productivity to 0
        if (!isPastOrCurrent) {
          hasData = false;
          productivity = 0;
        }
        
        // Create a unique identifier for this time period
        const timeLabel = `${dayNames[i]}${periodLabels[periodIndex]}`;
        
        dataPoints.push({
          time: timeLabel,
          hour: i * 10 + periodIndex, // Unique identifier for sorting
          productivity,
          hasData: hasData && isPastOrCurrent
        });
      }
    }

    return {
      dataPoints,
      startTime: 'Mån1',
      endTime: 'Sön6'
    };
  };

  const generateMonthlyData = (sortedScreenshots: any[]) => {
    // Group screenshots by date and time period (morning, afternoon, evening)
    const dayTimeGroups: { [key: string]: any[] } = {};
    
    sortedScreenshots.forEach(screenshot => {
      // Convert UTC timestamp to user timezone before extracting date/hour
      const utcTimestamp = screenshot.createdAt || screenshot.timestamp || screenshot.created_at;
      const userDate = timezoneService.convertUTCToUserTime(utcTimestamp);
      const day = userDate.getDate();
      const hour = userDate.getHours();
      
      // Divide day into 3 periods: morning (6-12), afternoon (12-18), evening (18-24+6)
      let period: string;
      if (hour >= 6 && hour < 12) {
        period = 'morning';
      } else if (hour >= 12 && hour < 18) {
        period = 'afternoon';
      } else {
        period = 'evening';
      }
      
      const dayTimeKey = `${day}-${period}`;
      
      if (!dayTimeGroups[dayTimeKey]) {
        dayTimeGroups[dayTimeKey] = [];
      }
      dayTimeGroups[dayTimeKey].push(screenshot);
    });

    const dataPoints: ProductivityDataPoint[] = [];
    // eslint-disable-next-line no-restricted-syntax
    const today = new Date(); // Current time for comparison
    // eslint-disable-next-line no-restricted-syntax
    const targetDate = currentDate || new Date(); // Use currentDate prop or today
    const currentDay = today.getDate();
    const currentHour = today.getHours();
    // eslint-disable-next-line no-restricted-syntax
    const daysInMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0).getDate();
    
    // Generate 3 data points for each day of the month (morning, afternoon, evening)
    for (let day = 1; day <= daysInMonth; day++) {
      const periods = ['morning', 'afternoon', 'evening'];
      const periodLabels = ['M', 'D', 'K']; // Morgon, Dag, Kväll
      
      periods.forEach((period, periodIndex) => {
        const dayTimeKey = `${day}-${period}`;
        const periodScreenshots = dayTimeGroups[dayTimeKey] || [];
        
        let productivity = 0;
        let hasData = periodScreenshots.length > 0;
        
        // Check if this time period has passed
        let isPastOrCurrent = false;
        if (day < currentDay) {
          isPastOrCurrent = true; // Past days are always valid
        } else if (day === currentDay) {
          // For today, check if the time period has started
          if (period === 'morning' && currentHour >= 6) isPastOrCurrent = true;
          else if (period === 'afternoon' && currentHour >= 12) isPastOrCurrent = true;
          else if (period === 'evening' && currentHour >= 18) isPastOrCurrent = true;
        }
        
        if (hasData && isPastOrCurrent) {
          const productivitySum = periodScreenshots.reduce((sum, screenshot) => {
            let screenshotProductivity = 50;
            
            try {
              let aiAnalysis: any = {};
              if (screenshot.ai_analysis) {
                aiAnalysis = typeof screenshot.ai_analysis === 'string' 
                  ? JSON.parse(screenshot.ai_analysis) 
                  : screenshot.ai_analysis;
              }
              
              const workType = aiAnalysis.workType || screenshot.workType || 'Other';
              const confidence = screenshot.confidence || aiAnalysis.confidence || 50;
              
              if (workType === 'Development' || workType === 'coding') {
                screenshotProductivity = Math.min(95, 70 + (confidence / 100) * 25);
              } else if (workType === 'Research' || workType === 'research') {
                screenshotProductivity = Math.min(85, 60 + (confidence / 100) * 25);
              } else if (workType === 'Documentation' || workType === 'documentation') {
                screenshotProductivity = Math.min(80, 55 + (confidence / 100) * 25);
              } else if (workType === 'Communication' || workType === 'meeting') {
                screenshotProductivity = Math.min(75, 50 + (confidence / 100) * 25);
              } else if (workType === 'Design' || workType === 'design') {
                screenshotProductivity = Math.min(90, 65 + (confidence / 100) * 25);
              } else {
                screenshotProductivity = Math.min(70, 40 + (confidence / 100) * 30);
              }
            } catch (e) {
              // Use default
            }
            
            return sum + screenshotProductivity;
          }, 0);
          
          productivity = Math.round(productivitySum / periodScreenshots.length);
        }
        
        // For future time periods, set hasData to false and productivity to 0
        if (!isPastOrCurrent) {
          hasData = false;
          productivity = 0;
        }
        
        // Create a unique identifier for this time period
        const timeLabel = `${day}${periodLabels[periodIndex]}`;
        
        dataPoints.push({
          time: timeLabel,
          hour: day * 10 + periodIndex, // Unique identifier for sorting
          productivity,
          hasData: hasData && isPastOrCurrent
        });
      });
    }

    return {
      dataPoints,
      startTime: '1M',
      endTime: `${daysInMonth}K`
    };
  };

  const getProductivityColor = (productivity: number) => {
    if (productivity >= 80) return '#27ae60'; // Green
    if (productivity >= 60) return '#f39c12'; // Orange
    if (productivity >= 40) return '#e67e22'; // Dark orange
    return '#e74c3c'; // Red
  };

  if (loading) {
    return (
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px' }}>
          Loading productivity graph...
        </Typography>
      </Box>
    );
  }

  if (dataPoints.length === 0) {
    return (
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px' }}>
          No tracking data today. Start taking screenshots to see your productivity graph!
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 1.5, 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    }}>
      {/* Chart with Y-axis labels */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flex: 1, minHeight: 0 }}>
        {/* Y-axis labels */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between', 
          height: '45px',
          mr: 1,
          minWidth: '35px'
        }}>
          <Typography variant="caption" sx={{ 
            fontSize: '10px', 
            color: '#64748b', 
            lineHeight: 1,
            fontWeight: 500,
            textAlign: 'right'
          }}>
            100%
          </Typography>
          <Typography variant="caption" sx={{ 
            fontSize: '10px', 
            color: '#64748b', 
            lineHeight: 1,
            fontWeight: 500,
            textAlign: 'right'
          }}>
            50%
          </Typography>
          <Typography variant="caption" sx={{ 
            fontSize: '10px', 
            color: '#64748b', 
            lineHeight: 1,
            fontWeight: 500,
            textAlign: 'right'
          }}>
            0%
          </Typography>
        </Box>

        {/* Chart container */}
        <Box sx={{ 
          position: 'relative', 
          height: '45px', 
          flex: 1,
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
          boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
          {dataPoints.length > 1 ? (
            <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
              {/* Enhanced background with subtle gradient */}
              <defs>
                <linearGradient id="bgGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#f8fafc" stopOpacity="0.8"/>
                  <stop offset="100%" stopColor="#ffffff" stopOpacity="1"/>
                </linearGradient>
                <pattern id="grid" width="20" height="15" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 15" fill="none" stroke="#e2e8f0" strokeWidth="0.5" opacity="0.6"/>
                </pattern>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              <rect width="100%" height="100%" fill="url(#bgGradient)" />
              <rect width="100%" height="100%" fill="url(#grid)" />
              
              {/* Area fill under the productivity line */}
              {dataPoints.length > 1 && (
                <path
                  d={`M ${5} ${55 - (dataPoints[0].productivity / 100) * 50} ${dataPoints.map((point, index) => {
                    const x = (index / (dataPoints.length - 1)) * 85 + 5;
                    const y = 55 - (point.productivity / 100) * 50;
                    return `L ${x} ${y}`;
                  }).join(' ')} L ${95} 55 L ${5} 55 Z`}
                  fill="url(#areaGradient)"
                  opacity="0.3"
                />
              )}
              
              {/* Gradient for area fill */}
              <defs>
                <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4"/>
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.1"/>
                </linearGradient>
              </defs>
              
              {/* Enhanced productivity line */}
              {dataPoints.length > 1 && (
                <>
                  {dataPoints.map((point, index) => {
                    if (index === 0) return null;
                    
                    const prevPoint = dataPoints[index - 1];
                    const x1 = ((index - 1) / (dataPoints.length - 1)) * 85 + 5;
                    const y1 = 55 - (prevPoint.productivity / 100) * 50;
                    const x2 = (index / (dataPoints.length - 1)) * 85 + 5;
                    const y2 = 55 - (point.productivity / 100) * 50;
                    
                    return (
                      <line
                        key={`line-${index}`}
                        x1={`${x1}%`}
                        y1={y1}
                        x2={`${x2}%`}
                        y2={y2}
                        stroke={(!prevPoint.hasData || !point.hasData) ? "#cbd5e1" : "#3b82f6"}
                        strokeWidth={(!prevPoint.hasData || !point.hasData) ? "1.5" : "3"}
                        strokeLinecap="round"
                        strokeDasharray={(!prevPoint.hasData || !point.hasData) ? "3,3" : "none"}
                        opacity={(!prevPoint.hasData || !point.hasData) ? 0.6 : 0.9}
                      />
                    );
                  })}
                </>
              )}
              
              {/* Enhanced data points with hover effects */}
              {dataPoints.map((point, index) => {
                const x = (index / (dataPoints.length - 1)) * 85 + 5;
                const y = 55 - (point.productivity / 100) * 50;
                
                return (
                  <g key={index}>
                    {point.hasData && (
                      <circle
                        cx={`${x}%`}
                        cy={y}
                        r="6"
                        fill={getProductivityColor(point.productivity)}
                        opacity="0.2"
                      />
                    )}
                    <circle
                      cx={`${x}%`}
                      cy={y}
                      r={point.hasData ? 4 : 2}
                      fill={point.hasData ? getProductivityColor(point.productivity) : '#cbd5e1'}
                      stroke="#ffffff"
                      strokeWidth={point.hasData ? "2" : "1"}
                      opacity={point.hasData ? 1 : 0.7}
                      filter={point.hasData ? "url(#glow)" : "none"}
                      style={{ cursor: point.hasData ? 'pointer' : 'default' }}
                    />
                  </g>
                );
              })}
            </svg>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              color: '#999',
              fontSize: '10px'
            }}>
              No data points available
            </Box>
          )}
        </Box>
      </Box>

      {/* X-axis (Time) labels - show hourly marks for daily view, daily marks for weekly view */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        px: 4, 
        mb: 1,
        minHeight: '14px',
        borderTop: '1px solid #e2e8f0',
        pt: 1
      }}>
        {(() => {
          if (!startTime || !endTime) {
            return (
              <Typography variant="caption" sx={{ fontSize: '9px', color: '#666' }}>
                No time data
              </Typography>
            );
          }
          
          // Determine view type based on timeRange
          const isMonthlyView = timeRange === '30d' || timeRange === '1m';
          const isWeeklyView = timeRange === '7d' || timeRange === '1w';
          const isDailyView = timeRange === '1d';
          
          if (isMonthlyView) {
            // For monthly view, show dates but limit to current date
            // eslint-disable-next-line no-restricted-syntax
            const today = new Date(); // Get current time for month day comparison
            const currentDate = today.getDate();
            const allMonthlyMarks = [1, 5, 10, 15, 20, 25, 30];
            
            // Filter out future dates
            const validMonthlyMarks = allMonthlyMarks.filter(date => date <= currentDate);
            
            // If no valid marks or only one, add current date
            if (validMonthlyMarks.length <= 1 || !validMonthlyMarks.includes(currentDate)) {
              if (!validMonthlyMarks.includes(currentDate)) {
                validMonthlyMarks.push(currentDate);
              }
              validMonthlyMarks.sort((a, b) => a - b);
            }
            
            const monthlyMarks = validMonthlyMarks.map((date, index) => ({
              time: date.toString(),
              isStart: index === 0,
              isToday: date === currentDate
            }));
            
            return monthlyMarks.map((mark, index) => (
              <Typography 
                key={index}
                variant="caption" 
                sx={{ 
                  fontSize: '9px', 
                  color: mark.isToday ? '#333' : (mark.isStart ? '#333' : '#666'),
                  fontWeight: mark.isToday ? 600 : (mark.isStart ? 600 : 400),
                  flex: '1 1 0',
                  textAlign: 'center'
                }}
              >
                {mark.time}
              </Typography>
            ));
          } else if (isWeeklyView) {
            // For weekly view, show days instead of hours
            const dayNames = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
            // eslint-disable-next-line no-restricted-syntax
            const today = new Date(); // Get current time for weekday comparison
            const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
            
            // Create marks for each day of the week, but gray out future days
            const dailyMarks = dayNames.map((dayName, index) => {
              const dayIndex = index + 1; // Convert to 1-7 (Monday = 1, Sunday = 7)
              const adjustedDayIndex = dayIndex === 7 ? 0 : dayIndex; // Convert Sunday back to 0
              const isPastOrToday = adjustedDayIndex <= currentDayOfWeek || (currentDayOfWeek === 0 && adjustedDayIndex === 0);
              
              return {
                time: dayName,
                isStart: index === 0,
                isPastOrToday
              };
            });
            
            return dailyMarks.map((mark, index) => (
              <Typography 
                key={index}
                variant="caption" 
                sx={{ 
                  fontSize: '9px', 
                  color: mark.isPastOrToday ? (mark.isStart ? '#333' : '#666') : '#ccc',
                  fontWeight: mark.isStart ? 600 : 400,
                  flex: '1 1 0',
                  textAlign: 'center'
                }}
              >
                {mark.time}
              </Typography>
            ));
          } else {
            // For daily view, show hourly marks as before
            const [startHour, startMin] = startTime.split(':').map(Number);
            const [endHour, endMin] = endTime.split(':').map(Number);
            
            const startHourRounded = startMin > 0 ? startHour + 1 : startHour; // Start from next full hour if not on the hour
            const endHourRounded = endMin > 0 ? endHour : endHour - 1; // End at previous full hour if not on the hour
            
            const hourlyMarks = [];
            
            // Always show start time
            hourlyMarks.push({
              time: startTime,
              isStart: true
            });
            
            // Add hourly marks
            for (let hour = startHourRounded; hour <= endHourRounded; hour++) {
              const timeStr = `${hour.toString().padStart(2, '0')}:00`;
              if (timeStr !== startTime && timeStr !== endTime) {
                hourlyMarks.push({
                  time: timeStr,
                  isStart: false
                });
              }
            }
            
            // Always show end time
            if (endTime !== startTime) {
              hourlyMarks.push({
                time: endTime,
                isStart: false
              });
            }
            
            // Limit to max 5 marks to avoid overcrowding
            const maxMarks = 5;
            let displayMarks = hourlyMarks;
            
            if (hourlyMarks.length > maxMarks) {
              // Keep start and end, distribute others evenly
              const step = Math.ceil((hourlyMarks.length - 2) / (maxMarks - 2));
              displayMarks = [
                hourlyMarks[0], // start
                ...hourlyMarks.slice(1, -1).filter((_, index) => index % step === 0),
                hourlyMarks[hourlyMarks.length - 1] // end
              ];
            }
            
            return displayMarks.map((mark, index) => (
              <Typography 
                key={index}
                variant="caption" 
                sx={{ 
                  fontSize: '9px', 
                  color: mark.isStart ? '#333' : '#666',
                  fontWeight: mark.isStart ? 600 : 400,
                  flex: index === 0 || index === displayMarks.length - 1 ? '0 0 auto' : '1 1 0',
                  textAlign: index === 0 ? 'left' : index === displayMarks.length - 1 ? 'right' : 'center'
                }}
              >
                {mark.time}
              </Typography>
            ));
          }
        })()}
      </Box>

      {/* Summary display */}
      <Box sx={{ 
        px: 1, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        py: 0.3,
        mx: 1,
        mt: 'auto'
      }}>
        <Typography variant="caption" sx={{ 
          fontSize: '10px', 
          color: '#2c3e50', 
          fontWeight: 600 
        }}>
          {(() => {
            const isMonthlyView = timeRange === '30d' || timeRange === '1m';
            const isWeeklyView = timeRange === '7d' || timeRange === '1w';
            const isDailyView = timeRange === '1d';
            
            if (isMonthlyView) {
              // Use currentDate prop for month calculation instead of always today
              // eslint-disable-next-line no-restricted-syntax
              const targetDate = currentDate || new Date(); // Get date for formatting
              return `📅 ${timezoneService.formatUserDate(targetDate, { month: 'long', year: 'numeric' })}`;
            } else if (isWeeklyView) {
              // Use currentDate prop for week calculation instead of always today
              // eslint-disable-next-line no-restricted-syntax
              const targetDate = currentDate || new Date(); // Get date for week calculation
              // eslint-disable-next-line no-restricted-syntax
              const monday = new Date(targetDate); // Clone for manipulation
              // Adjust for Swedish week (Monday = 1, Sunday = 0)
              const dayOfWeek = targetDate.getDay();
              const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 6 days from Monday
              // Use timezone-safe date manipulation
              monday.setUTCDate(targetDate.getUTCDate() - daysFromMonday);
              
              // eslint-disable-next-line no-restricted-syntax
              const sunday = new Date(monday); // Clone for end of week
              sunday.setUTCDate(monday.getUTCDate() + 6);
              
              // Format dates - if same month, show "22 - 28 jul", if different months show "28 jul - 3 aug"
              const mondayDay = monday.getDate();
              const sundayDay = sunday.getDate();
              const mondayMonth = timezoneService.formatUserDate(monday, { month: 'short' });
              const sundayMonth = timezoneService.formatUserDate(sunday, { month: 'short' });
              
              if (mondayMonth === sundayMonth) {
                return `📅 ${mondayDay} - ${sundayDay} ${mondayMonth}`;
              } else {
                return `📅 ${mondayDay} ${mondayMonth} - ${sundayDay} ${sundayMonth}`;
              }
            } else {
              // Daily view - show time range as before
              return `🕐 ${startTime && endTime ? `${startTime} - ${endTime}` : 'No time data'}`;
            }
          })()}
        </Typography>
        <Typography variant="caption" sx={{ 
          fontSize: '10px', 
          color: '#27ae60', 
          fontWeight: 600 
        }}>
          📊 {dataPoints.length > 0 ? Math.round(dataPoints.filter(p => p.hasData).reduce((sum, p) => sum + p.productivity, 0) / Math.max(dataPoints.filter(p => p.hasData).length, 1)) : 0}%
        </Typography>
      </Box>
    </Box>
  );
};

export default ProductivityTimeline;
